import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  Folder,
  Check,
  FolderOpenDot,
  Plus,
  FolderOpen,
  Edit3,
  Trash2,
} from "lucide-react";
import documentStoresService, {
  DocumentStore,
  UpdateDocumentStoreData,
  DocumentStoreListParams,
  DocumentStoreTypeEnums,
} from "../../services/document-stores.service";
import Loader from "../../widgets/Loader";
import CreateFolderModal from "../../components/documents/CreateFolderModal";
import DeleteConfirmModal from "../../components/documents/DeleteConfirmModal";
import Pagination from "../../components/documents/Pagination";
import DocumentsHeader from "../../components/documents/DocumentsHeader";
import SelectionToolbar from "../../components/documents/SelectionToolbar";
import CloudStorageConnect from "../../components/documents/CloudStorageConnect";
import Tooltip from "../../widgets/ToolTip";
import Can from "../../components/auth/Can";
import {
  getSourceIcon,
  getDisplayName,
  formatDate,
  DocumentStoreType,
} from "../../utils/documentHelpers";

interface FolderItem {
  id: string;
  name: string;
  description: string;
  total_file_count: number;
  createdAt: string;
  source: DocumentStoreType;
  document_store_type?: string;
}

const Documents: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState<"list" | "grid">("grid");
  const [activeTab, setActiveTab] = useState<DocumentStoreType>("All");
  const [folders, setFolders] = useState<FolderItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingFolders, setIsLoadingFolders] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(20);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newFolder, setNewFolder] = useState({ name: "", description: "" });
  const [editingFolder, setEditingFolder] = useState<FolderItem | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState<FolderItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedFolders, setSelectedFolders] = useState<Set<string>>(
    new Set()
  );
  const [googleDriveToken, setGoogleDriveToken] = useState<string | null>(null);
  const [dropboxToken, setDropboxToken] = useState<string | null>(null);
  const [onedriveToken, setOnedriveToken] = useState<string | null>(null);

  const isInitialMount = useRef(true);

  const fetchFolders = async () => {
    try {
      setIsLoadingFolders(true);
      const params: DocumentStoreListParams = {
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
        ...(searchQuery && { folder_name: searchQuery }),
        ...(activeTab !== "All" && { document_store_type: activeTab }),
      };

      const response = await documentStoresService.getDocumentStores(params);

      const mappedFolders: FolderItem[] = response.data.document_stores.map(
        (store: DocumentStore) => ({
          id: store._id,
          name: store.folder_name,
          description: store.folder_description || "",
          total_file_count: store.total_file_count || 0, // API doesn't provide file count yet
          createdAt: store.createdAt,
          source: (store.document_store_type || "LOCAL") as DocumentStoreType,
          document_store_type: store.document_store_type || "LOCAL",
        })
      );

      setFolders(mappedFolders);
      setTotalCount(response.data.total_count);
    } catch (error: any) {
      console.error("Error fetching folders:", error);
      if (error.response?.status === 401) {
        let errorMessage = "";
        switch (activeTab) {
          case "GOOGLE DRIVE":
            localStorage.removeItem("google_drive_token");
            setGoogleDriveToken(null);
            errorMessage = "Google Drive token expired. Please reconnect.";
            break;
          case "DROPBOX":
            localStorage.removeItem("dropbox_token");
            setDropboxToken(null);
            errorMessage = "Dropbox token expired. Please reconnect.";
            break;
          case "ONEDRIVE":
            localStorage.removeItem("onedrive_token");
            setOnedriveToken(null);
            errorMessage = "OneDrive token expired. Please reconnect.";
            break;
          default:
            errorMessage = "Authentication token expired. Please reconnect.";
            break;
        }
        toast.error(errorMessage);
      } else {
        toast.error("Failed to load folders");
      }
    } finally {
      setIsLoadingFolders(false);
    }
  };

  const getFilteredFolders = () => {
    return folders;
  };

  useEffect(() => {
    const googleStatus = searchParams.get("google_drive_status");
    const googleToken = searchParams.get("google_drive_token");
    const dropboxStatus = searchParams.get("dropbox_status");
    const dropboxTokenParam = searchParams.get("dropbox_access_token");
    const onedriveStatus = searchParams.get("one_drive_status");
    const onedriveTokenParam = searchParams.get("access_token");

    if (googleStatus === "success" && googleToken) {
      localStorage.setItem("google_drive_token", googleToken);
      setGoogleDriveToken(googleToken);
      toast.success("Google Drive authorized successfully!");
      searchParams.delete("google_drive_status");
      searchParams.delete("google_drive_token");
      setSearchParams(searchParams);
    } else if (dropboxStatus === "success" && dropboxTokenParam) {
      localStorage.setItem("dropbox_token", dropboxTokenParam);
      setDropboxToken(dropboxTokenParam);
      toast.success("Dropbox authorized successfully!");
      searchParams.delete("dropbox_status");
      searchParams.delete("dropbox_access_token");
      setSearchParams(searchParams);
    } else if (onedriveStatus === "success" && onedriveTokenParam) {
      localStorage.setItem("onedrive_token", onedriveTokenParam);
      setOnedriveToken(onedriveTokenParam);
      toast.success("OneDrive authorized successfully!");
      searchParams.delete("onedrive_status");
      searchParams.delete("onedrive_token");
      setSearchParams(searchParams);
    } else {
      const savedGoogleToken = localStorage.getItem("google_drive_token");
      if (savedGoogleToken) {
        setGoogleDriveToken(savedGoogleToken);
      }
      const savedDropboxToken = localStorage.getItem("dropbox_token");
      if (savedDropboxToken) {
        setDropboxToken(savedDropboxToken);
      }
      const savedOnedriveToken = localStorage.getItem("onedrive_token");
      if (savedOnedriveToken) {
        setOnedriveToken(savedOnedriveToken);
      }
    }
  }, []);

  useEffect(() => {
    fetchFolders();
    const timer = setTimeout(() => {
      isInitialMount.current = false;
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isInitialMount.current) return;
    fetchFolders();
  }, [currentPage, activeTab]);

  useEffect(() => {
    if (isInitialMount.current) return;
    const debounceTimer = setTimeout(() => {
      setCurrentPage(1);
      fetchFolders();
    }, 500);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const handleCreateFolder = async () => {
    if (!newFolder.name.trim()) return;

    setIsLoading(true);
    try {
      if (editingFolder) {
        const updateData: UpdateDocumentStoreData = {
          folder_name: newFolder.name,
          folder_description: newFolder.description,
        };
        await documentStoresService.updateDocumentStore(
          editingFolder.id,
          updateData
        );
        toast.success(`${newFolder.name} updated successfully!`);
        setEditingFolder(null);
      } else {
        const createData = {
          folder_name: newFolder.name,
          folder_description: newFolder.description,
          document_store_type: DocumentStoreTypeEnums.LOCAL,
        };
        await documentStoresService.createDocumentStore(createData);
        toast.success(`${newFolder.name} created successfully!`);
      }

      setNewFolder({ name: "", description: "" });
      setShowCreateModal(false);
      await fetchFolders(); // Refresh the list
    } catch (error: any) {
      console.error("Error saving folder:", error);
      if (error.response?.data?.error?.error_description) {
        toast.error(error.response.data.error.error_description);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(
          editingFolder ? "Failed to update folder" : "Failed to create folder"
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditFolder = (folder: FolderItem) => {
    setEditingFolder(folder);
    setNewFolder({ name: folder.name, description: folder.description });
    setShowCreateModal(true);
  };

  const handleDeleteClick = (folder: FolderItem) => {
    setFolderToDelete(folder);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (folderToDelete) {
      setIsDeleting(true);
      try {
        await documentStoresService.deleteDocumentStore(folderToDelete.id);
        setSelectedFolders((prev) => {
          const newSet = new Set(prev);
          newSet.delete(folderToDelete.id);
          return newSet;
        });
        await fetchFolders(); // Refresh the list
        toast.success(`Folder "${folderToDelete.name}" deleted successfully!`);
        setFolderToDelete(null);
        setShowDeleteConfirm(false);
        setIsDeleting(false);
      } catch (error: any) {
        console.error("Error deleting folder:", error);
        setIsDeleting(false);
        if (error.response?.data?.error?.error_description) {
          toast.error(error.response.data.error.error_description);
        } else if (error.response?.data?.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error("Failed to delete folder");
        }
      }
    }
  };

  const handleCancelDelete = () => {
    setFolderToDelete(null);
    setShowDeleteConfirm(false);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setEditingFolder(null);
    setNewFolder({ name: "", description: "" });
  };

  const handleSelectFolder = (folderId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    const newSelected = new Set(selectedFolders);
    if (newSelected.has(folderId)) {
      newSelected.delete(folderId);
    } else {
      if (newSelected.size === 1) {
        newSelected.clear();
      }
      newSelected.add(folderId);
    }
    setSelectedFolders(newSelected);
  };

  const clearSelection = () => {
    setSelectedFolders(new Set());
  };

  const handleFolderClick = (folderId: string) => {
    if (selectedFolders.size > 0) {
      const newSelected = new Set(selectedFolders);
      if (newSelected.has(folderId)) {
        newSelected.delete(folderId);
      } else {
        newSelected.add(folderId);
      }
      setSelectedFolders(newSelected);
    } else {
      navigate(`/files/${folderId}`);
    }
  };

  const currentFilteredFolders = getFilteredFolders();
  const totalPages = Math.ceil(totalCount / pageSize);

  // Calculate folder counts for each storage type
  const folderCounts = {
    All: folders.length,
    LOCAL: folders.filter((f) => f.document_store_type === "LOCAL").length,
    "GOOGLE DRIVE": folders.filter(
      (f) => f.document_store_type === "GOOGLE DRIVE"
    ).length,
    DROPBOX: folders.filter((f) => f.document_store_type === "DROPBOX").length,
    ONEDRIVE: folders.filter((f) => f.document_store_type === "ONEDRIVE")
      .length,
  } as Record<DocumentStoreType, number>;

  return (
    <div className="min-h-screen bg-gray-50">
      <DocumentsHeader
        viewMode={viewMode}
        setViewMode={setViewMode}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        onCreateFolder={() => setShowCreateModal(true)}
        folderCounts={folderCounts}
      />

      {(activeTab === "GOOGLE DRIVE" ||
        activeTab === "DROPBOX" ||
        activeTab === "ONEDRIVE") && (
        <div className="mb-6">
          <CloudStorageConnect
            storageType={activeTab}
            googleDriveToken={googleDriveToken}
            setGoogleDriveToken={setGoogleDriveToken}
            dropboxToken={dropboxToken}
            setDropboxToken={setDropboxToken}
            onedriveToken={onedriveToken}
            setOnedriveToken={setOnedriveToken}
            onUploadSuccess={fetchFolders}
          />
        </div>
      )}

      <div className="py-2">
        {isLoadingFolders ? (
          <div className="flex items-center justify-center py-32">
            <Loader />
          </div>
        ) : (
          <>
            {/* Cloud Storage Connection UI - Show for cloud tabs */}

            <SelectionToolbar
              selectedFolders={selectedFolders}
              folders={folders}
              onClearSelection={clearSelection}
              onOpenFolder={(folderId) => navigate(`/files/${folderId}`)}
              onEditFolder={handleEditFolder}
              onDeleteFolder={handleDeleteClick}
            />

            {/* Folder List - Show already uploaded folders */}
            {currentFilteredFolders.length > 0 && (
              <>
                {/* Section Header for Cloud Folders */}
                {(activeTab === "GOOGLE DRIVE" ||
                  activeTab === "DROPBOX" ||
                  activeTab === "ONEDRIVE") && (
                  <div className="mb-4">
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      {getSourceIcon(activeTab)}
                      <h3 className="font-semibold">
                        Already Uploaded Folders from{" "}
                        {getDisplayName(activeTab)}
                      </h3>
                      <span className="text-xs text-gray-500">
                        ({currentFilteredFolders.length})
                      </span>
                    </div>
                  </div>
                )}

                {viewMode === "list" ? (
                  <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                    <div className="bg-gray-50 px-6 py-3">
                      <div className="grid grid-cols-12 gap-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="col-span-3">Name</div>
                        <div className="col-span-3">Description</div>
                        <div className="col-span-2">Type</div>
                        <div className="col-span-1">Files</div>
                        <div className="col-span-2">Created</div>
                        <div className="col-span-1">Action</div>
                      </div>
                    </div>

                    <div className="divide-y divide-gray-200">
                      {currentFilteredFolders.length > 0 ? (
                        currentFilteredFolders.map((folder) => (
                          <div
                            key={folder.id}
                            className={`grid grid-cols-12 gap-4 px-6 py-4 cursor-pointer transition-colors ${
                              selectedFolders.has(folder.id)
                                ? "bg-blue-50"
                                : "hover:bg-gray-50"
                            }`}
                            onClick={() => handleFolderClick(folder.id)}
                          >
                            <div className="col-span-3 flex items-center gap-3">
                              <input
                                type="checkbox"
                                checked={selectedFolders.has(folder.id)}
                                onChange={(e) =>
                                  handleSelectFolder(folder.id, e as any)
                                }
                                onClick={(e) => e.stopPropagation()}
                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                              />
                              <svg
                                className="w-6 h-6 text-yellow-500"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                              </svg>
                              <div>
                                <div className="font-medium text-gray-900">
                                  {folder.name}
                                </div>
                              </div>
                            </div>
                            <div className="col-span-3 flex items-center">
                              <span className="text-sm text-gray-600">
                                {folder.description || "No description"}
                              </span>
                            </div>
                            <div className="col-span-2 flex items-center gap-2">
                              {getSourceIcon(
                                folder.document_store_type || folder.source
                              )}
                              <span className="text-sm text-gray-600">
                                {getDisplayName(
                                  folder.document_store_type || folder.source
                                )}
                              </span>
                            </div>
                            <div className="col-span-1 flex items-center">
                              <span className="text-sm text-gray-600">
                                {folder?.total_file_count}
                              </span>
                            </div>
                            <div className="col-span-2 flex items-center justify-between">
                              <span className="text-sm text-gray-600">
                                {formatDate(folder.createdAt)}
                              </span>
                            </div>
                            <div className="col-span-1 flex items-center justify-start">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/files/${folder.id}`);
                                }}
                                className="p-1.5 hover:bg-blue-100 rounded-lg transition-colors"
                                title="Open folder"
                              >
                                <FolderOpen className="h-4 w-4 text-blue-600" />
                              </button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="px-6 py-4 text-center text-gray-500">
                          No folders to display.
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="mt-2">
                    {currentFilteredFolders.length > 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {currentFilteredFolders.map((folder) => (
                          <div
                            key={folder.id}
                            className={`group relative cursor-pointer rounded-xl border bg-white p-4 transition-all duration-200 h-[88px] 
            ${
              selectedFolders.has(folder.id)
                ? "border-2 border-blue-500 bg-blue-50 shadow-md" // Enhanced selected style
                : "border-gray-200 hover:shadow-lg hover:border-gray-300" // Normal/hover style
            }`}
                            onClick={(e) => handleSelectFolder(folder.id, e)}
                          >
                            {selectedFolders.has(folder.id) && (
                              <div className="absolute top-0 left-0 z-10 -translate-x-1/2 -translate-y-1/2">
                                <div className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-600 text-white">
                                  <Check className="h-4 w-4" />
                                </div>
                              </div>
                            )}

                            <div className="absolute top-3 right-3 flex items-center gap-1.5 opacity-100 group-hover:opacity-0 transition-opacity">
                              {getSourceIcon(
                                folder.document_store_type || folder.source
                              )}
                              <span className="text-xs text-gray-600">
                                {getDisplayName(
                                  folder.document_store_type || folder.source
                                )}
                              </span>
                            </div>

                            <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Tooltip
                                content="View Files"
                                position="bottom"
                                delay={200}
                              >
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/files/${folder.id}`);
                                  }}
                                  className="flex items-center gap-1 px-2 py-1 bg-white hover:bg-gray-200 rounded-md shadow-sm border border-gray-200 transition-all transform hover:scale-105"
                                >
                                  <FolderOpenDot className="h-5 w-5 text-blue-500" />
                                </button>
                              </Tooltip>
                            </div>

                            <div className="flex items-center gap-3">
                              <div className="flex-shrink-0">
                                <svg
                                  className="w-8 h-8 text-yellow-500"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                                </svg>
                              </div>

                              <div className="flex-1 min-w-0">
                                {folder.name.length >
                                (folder.document_store_type === "GOOGLE DRIVE"
                                  ? 10
                                  : 15) ? (
                                  <Tooltip content={folder.name}>
                                    <h3 className="text-sm font-medium text-gray-900 truncate leading-tight mb-1">
                                      {folder.name.slice(
                                        0,
                                        folder.document_store_type ===
                                          "GOOGLE DRIVE"
                                          ? 10
                                          : 15
                                      )}
                                      ...
                                    </h3>
                                  </Tooltip>
                                ) : (
                                  <h3 className="text-sm font-medium text-gray-900 truncate leading-tight mb-1">
                                    {folder.name}
                                  </h3>
                                )}

                                <p className="text-xs text-gray-500 truncate leading-tight">
                                  {folder.description || "No description"}
                                </p>
                              </div>
                            </div>

                            <div className="hidden">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditFolder(folder);
                                }}
                              >
                                <Edit3 className="h-4 w-4" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(folder);
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        No folders to display.
                      </div>
                    )}
                  </div>
                )}

                {/* Pagination */}
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalCount={totalCount}
                  pageSize={pageSize}
                  onPageChange={handlePageChange}
                />
              </>
            )}
          </>
        )}
      </div>

      {/* Create Folder Modal */}
      <CreateFolderModal
        isOpen={showCreateModal}
        onClose={handleCloseModal}
        folderData={newFolder}
        setFolderData={setNewFolder}
        onSubmit={handleCreateFolder}
        isLoading={isLoading}
        isEditing={!!editingFolder}
      />

      {/* Confirmation Modal for Delete */}
      <DeleteConfirmModal
        isOpen={showDeleteConfirm}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        itemName={folderToDelete?.name || ""}
        itemType="Folder"
        isDeleting={isDeleting}
      />

      {/* Empty State */}
      {!isLoadingFolders && currentFilteredFolders.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-32 h-32 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <Folder className="w-16 h-16 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchQuery ? "No folders found" : "No folders yet"}
          </h3>
          <p className="text-gray-600 text-center mb-6">
            {searchQuery
              ? `No folders match "${searchQuery}". Try a different search term.`
              : "Get started by creating your first folder to organize your documents."}
          </p>
          {!searchQuery && (
            <Can permission="documents:create">
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" />
              Create Your First Folder
            </button>
            </Can>
          )}
        </div>
      )}
    </div>
  );
};

export default Documents;
